/* eslint-disable no-console */
import { useEffect, useState } from 'react';

import type { MockScope } from '@/mocks/browser';

/**
 * Build-time mock flags.
 *
 * These MUST read `import.meta.env.*` directly instead of the `@/env/env` object. Vite
 * replaces a raw `import.meta.env.VITE_*` member expression with a string literal at build
 * time, so `MSW_ENABLED` folds to a constant and Rollup dead-code-eliminates the entire
 * `@/mocks/browser` graph — MSW runtime, every handler, every fake fixture — out of any
 * build that sets neither flag. Reading `env.VITE_MOCK_MODE` hides the value behind a
 * `createEnv` object property that Rollup cannot fold, which shipped an ~836 kB mock chunk
 * (fake vessels and documents included) into every production bundle.
 *
 * Mock data must never reach real production. Keep these literals literal.
 */
const MOCK_MODE_ENABLED = import.meta.env.VITE_MOCK_MODE === 'true';
const HSQE_MOCK_ENABLED = import.meta.env.VITE_HSQE_MOCK === 'true';
const MSW_ENABLED = MOCK_MODE_ENABLED || HSQE_MOCK_ENABLED;

/**
 * `VITE_MOCK_MODE` mocks the whole app and stubs auth; `VITE_HSQE_MOCK` mocks only `/hsqe/*`
 * and leaves the real Clerk provider tree and `/sx-next` base path intact so every other
 * module keeps hitting the real backend. When both are set the broader scope wins.
 */
const MOCK_SCOPE: MockScope = MOCK_MODE_ENABLED ? 'all' : 'hsqe-only';

export const useMockServiceWorker = () => {
  const [isReady, setIsReady] = useState(!MSW_ENABLED);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!MSW_ENABLED) {
      return;
    }
    async function initMSW() {
      try {
        const { createWorker } = await import('@/mocks/browser');

        await createWorker(MOCK_SCOPE).start({
          // 'warn' is load-bearing for the HSQE hybrid: unhandled requests pass through to
          // the real backend rather than being blocked.
          onUnhandledRequest: 'warn',
          quiet: false,
          serviceWorker: {
            url: '/mockServiceWorker.js'
          }
        });

        console.log(
          MOCK_SCOPE === 'hsqe-only'
            ? '🔶 HSQE mock mode enabled - MSW intercepting /hsqe/* only; all other modules use the real backend'
            : '🔶 Mock mode enabled - MSW intercepting API requests'
        );
        setIsReady(true);
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to start MSW');
        console.error('❌ Failed to start MSW:', error);
        setError(error);
        setIsReady(true);
      }
    }

    void initMSW();
  }, []);

  return { isReady, error };
};
